import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import App from "./App.tsx";
import { installCrossSurfaceHistoryGuard } from "@/lib/cross-surface-history";
import { scheduleIdleTask } from "@/lib/scheduleIdleTask";
import "./index.css";

installCrossSurfaceHistoryGuard();

// Initialize Sentry before rendering
// Environment comes from VITE_ENVIRONMENT (e.g. staging / production) so Sentry tags errors correctly
const sentryEnvironment =
  import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || "development";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: sentryEnvironment,
  // CefSharp (Outlook SafeLink scanner) fires this as an unhandled rejection when its JS bridge
  // loses track of a DOM node during React re-renders. The SDK's default filter only covers
  // MethodName:simulateEvent; this regex covers all MethodName variants.
  ignoreErrors: [
    /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/,
  ],
  // Performance sampling: 10% in production, 100% otherwise (staging/dev)
  tracesSampleRate: sentryEnvironment === "production" ? 0.1 : 1.0,
  // Session replay: 10% of sessions, 100% on errors
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Privacy-first: do not send PII by default
  sendDefaultPii: false,
});

createRoot(document.getElementById("root")!).render(<App />);

scheduleIdleTask(() => {
  Sentry.addIntegration(
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  );
  Sentry.addIntegration(Sentry.replayIntegration());

  void import("@/lib/pixels").then(({ loadPixels }) => loadPixels());
});
